import { PublicLayout } from '@/components/PublicLayout';
import { LogoMark, SectionEyebrow } from '@/components/ui';
import { LegalSection, LegalLink } from './MentionsLegalesPage';
import { Receipt } from 'lucide-react';
import { MENTION_TVA, MENTION_TVA_EVOLUTION } from '@roronoa-collectivites/shared-types';

/**
 * Conditions Générales de Vente — Vision Pro Collectivité.
 *
 * ⚠️ Ne pas confondre avec les CGU :
 *   - les CGU régissent l'USAGE du service par les administrés et les agents
 *     (gratuit pour l'administré, financé par la collectivité) ;
 *   - les présentes CGV régissent la RELATION COMMERCIALE entre One For All
 *     Distribution et la collectivité cliente (prix, durée, facturation,
 *     résiliation, réversibilité).
 *
 * Contexte B2G : le Client est une personne publique. En découlent des règles
 * impératives que les CGV ne peuvent pas écarter :
 *   - délai global de paiement de 30 jours, intérêts moratoires et indemnité de
 *     40 € dus de plein droit sans mise en demeure ;
 *   - facturation obligatoire via Chorus Pro ;
 *   - compétence du tribunal administratif ;
 *   - faculté de résiliation unilatérale pour motif d'intérêt général.
 *
 * Source de vérité : md/03_cgv.md du pack de conformité (v1.1). Toute
 * modification doit être répercutée dans les deux documents.
 */
export function CgvPage() {
  return (
    <PublicLayout maxWidth="lg" centered={false}>
      <article className="space-y-6 py-4 animate-fade-in">
        {/* Header */}
        <header className="flex items-start gap-3 pb-4 border-b border-border">
          <LogoMark icon={Receipt} accent="tech" size="lg" />
          <div className="flex-1">
            <SectionEyebrow as="div">Commande publique</SectionEyebrow>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-r-tight text-ink">
              Conditions générales de vente
            </h1>
            <p className="mt-2 text-sm text-ink-subtle leading-relaxed">
              Version <strong className="text-ink">1.2</strong> — Dernière mise à jour&nbsp;:
              23&nbsp;juillet&nbsp;2026.
            </p>
            <p className="mt-2 text-sm text-ink-muted leading-relaxed">
              Ces conditions encadrent l'abonnement souscrit par une collectivité territoriale ou un
              EPCI. L'usage du service par les administrés et les agents relève des{' '}
              <LegalLink href="/cgu">conditions générales d'utilisation</LegalLink>.
            </p>
          </div>
        </header>

        {/* ============ 1 ============ */}
        <LegalSection number="Art. 1" title="Objet et champ d'application">
          <p className="text-sm text-ink-muted leading-relaxed">
            Les présentes conditions générales de vente régissent la fourniture de l'abonnement au
            service Vision Pro Collectivité (logiciel en mode SaaS) à une collectivité territoriale
            ou un établissement public de coopération intercommunale (le «&nbsp;Client&nbsp;»).
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Elles s'appliquent sous réserve des règles impératives du Code de la commande publique.
            En cas de contradiction, l'ordre de prévalence est le suivant&nbsp;:
          </p>
          <ol className="list-decimal pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>les clauses particulières du marché ou du bon de commande&nbsp;;</li>
            <li>le cahier des clauses administratives générales applicable s'il est visé&nbsp;;</li>
            <li>le contrat de sous-traitance RGPD annexé&nbsp;;</li>
            <li>les présentes conditions générales de vente.</li>
          </ol>
        </LegalSection>

        {/* ============ 2 ============ */}
        <LegalSection number="Art. 2" title="Nature du service">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le service est fourni en mode hébergé (SaaS). Il comprend&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>l'accès aux modules souscrits pour les agents et les administrés de la collectivité&nbsp;;</li>
            <li>
              l'hébergement des données dans l'Union européenne
              (<strong className="text-ink">Francfort, Allemagne</strong>)&nbsp;;
            </li>
            <li>
              les mises à jour correctives et évolutives publiées pendant la durée de l'abonnement,
              <strong className="text-ink"> sans surcoût</strong>&nbsp;;
            </li>
            <li>les sauvegardes automatiques décrites à l'article 11&nbsp;;</li>
            <li>le support décrit à l'article 10.</li>
          </ul>
        </LegalSection>

        {/* ============ 3 ============ */}
        <LegalSection number="Art. 3" title="Commande et formation du contrat">
          <p className="text-sm text-ink-muted leading-relaxed">
            La souscription résulte de la signature d'un bon de commande émis par le Client, d'un
            devis accepté ou d'un marché. Le contrat est formé à la date de notification au titulaire.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Le Client communique à l'éditeur, préalablement à la première facturation, les références
            nécessaires à la transmission des factures via Chorus&nbsp;Pro&nbsp;: SIRET du service
            acheteur, code service le cas échéant, et{' '}
            <strong className="text-ink">numéro d'engagement juridique</strong> lorsque celui-ci est
            exigé par son comptable public. À défaut, la facture ne peut être déposée et le délai
            global de paiement ne court pas.
          </p>
        </LegalSection>

        {/* ============ 4 ============ */}
        <LegalSection number="Art. 4" title="Tarifs">
          <h3 className="font-display text-base font-semibold text-ink">4.1 Abonnement</h3>
          <p className="text-sm text-ink-muted leading-relaxed">
            L'abonnement est facturé selon la grille en vigueur au jour de la signature, fonction de
            la strate de population de la collectivité et des modules souscrits.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">
              La strate applicable est celle constatée à la date de signature du contrat.
            </strong>{' '}
            Elle demeure inchangée pendant toute la durée du contrat et de sa poursuite&nbsp;: une
            évolution de la population du Client n'entraîne aucune modification tarifaire.
          </p>

          <h3 className="font-display text-base font-semibold text-ink">4.2 Mise en service</h3>
          <p className="text-sm text-ink-muted leading-relaxed">
            Une prestation de mise en service est facturée une seule fois, au démarrage, selon la
            grille en vigueur. Elle est ferme et forfaitaire, et comprend&nbsp;:
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>le paramétrage initial de l'application aux besoins de la collectivité&nbsp;;</li>
            <li>l'assistance à la reprise des données existantes&nbsp;;</li>
            <li>
              un accompagnement à la prise en main d'une durée maximale de{' '}
              <strong className="text-ink">quatorze&nbsp;(14) heures</strong>, réparties en deux
              sessions de sept heures.
            </li>
          </ul>
          <p className="text-sm text-ink-muted leading-relaxed">
            Cet accompagnement consiste en des explications d'usage et une aide au paramétrage.{' '}
            <strong className="text-ink">
              Il ne constitue pas une action de formation professionnelle
            </strong>{' '}
            au sens du Code du travail. Une prestation de formation des agents peut être proposée
            séparément, sur devis.
          </p>

          <h3 className="font-display text-base font-semibold text-ink">
            4.3 Taxes — régime actuel et évolution prévisible
          </h3>
          <p className="text-sm text-ink-muted leading-relaxed">
            Les prix sont exprimés en euros et <strong className="text-ink">hors taxes</strong>.{' '}
            {/* Mention pilotée par FRANCHISE_TVA (packages/shared-types) : une seule
                ligne à changer le jour de l'assujettissement. */}
            <strong className="text-ink">{MENTION_TVA}</strong>&nbsp;: aucune taxe ne s'ajoute
            aujourd'hui aux montants indiqués, qui correspondent donc aux sommes effectivement dues
            par le Client.
          </p>
          {MENTION_TVA_EVOLUTION && (
            <>
              <p className="text-sm text-ink-muted leading-relaxed">
                <strong className="text-ink">
                  Assujettissement futur — information du Client.
                </strong>{' '}
                Le régime de franchise dont relève l'éditeur est lié à son niveau d'activité. Le
                franchissement du seuil entraînera son assujettissement de plein droit à la TVA. Le
                Client est expressément informé, dès la signature, que&nbsp;:
              </p>
              <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
                <li>
                  <strong className="text-ink">
                    le tarif hors taxes indiqué au contrat restera inchangé
                  </strong>{' '}
                  ; la taxe au taux légal en vigueur (20&nbsp;% à ce jour) s'y ajoutera&nbsp;;
                </li>
                <li>
                  à titre d'illustration, un abonnement de 29&nbsp;€ HT par mois sera facturé
                  34,80&nbsp;€ TTC&nbsp;;
                </li>
                <li>
                  le Client en sera informé{' '}
                  <strong className="text-ink">au moins trente&nbsp;(30) jours avant</strong> la
                  date d'effet&nbsp;;
                </li>
                <li>
                  cet assujettissement résulte d'une obligation fiscale et{' '}
                  <strong className="text-ink">ne constitue pas une révision tarifaire</strong> au
                  sens de l'article 4.4 : il n'ouvre pas droit à la faculté de résiliation prévue à
                  cet article.
                </li>
              </ul>
              <p className="text-sm text-ink-muted leading-relaxed">
                Il appartient au Client de tenir compte de cette évolution dans la programmation de
                ses crédits budgétaires.
              </p>
            </>
          )}

          <h3 className="font-display text-base font-semibold text-ink">4.4 Évolution des tarifs</h3>
          <p className="text-sm text-ink-muted leading-relaxed">
            L'éditeur peut faire évoluer sa grille tarifaire. Toute modification est notifiée au
            Client au moins <strong className="text-ink">trois&nbsp;(3) mois</strong> avant son
            entrée en vigueur, laquelle intervient au plus tôt à l'échéance de la période
            contractuelle en cours. Le Client qui n'accepte pas le nouveau tarif peut résilier sans
            indemnité avant cette date.
          </p>
        </LegalSection>

        {/* ============ 5 ============ */}
        <LegalSection number="Art. 5" title="Facturation et paiement">
          <p className="text-sm text-ink-muted leading-relaxed">
            La facturation de l'abonnement est{' '}
            <strong className="text-ink">mensuelle, à terme échu</strong>. La mise en service est
            facturée après sa réalisation.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Les factures sont transmises par voie électronique via{' '}
            <strong className="text-ink">Chorus&nbsp;Pro</strong>, conformément à l'obligation
            applicable aux fournisseurs des personnes publiques.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Le paiement intervient selon les règles de la comptabilité publique. Le délai global de
            paiement est celui fixé par la réglementation, soit{' '}
            <strong className="text-ink">trente&nbsp;(30) jours</strong> pour les collectivités
            territoriales et leurs établissements publics. Le dépassement de ce délai ouvre droit, de
            plein droit et sans formalité, aux intérêts moratoires au taux réglementaire ainsi qu'à
            l'indemnité forfaitaire pour frais de recouvrement de 40&nbsp;euros.
          </p>
        </LegalSection>

        {/* ============ 6 ============ */}
        <LegalSection number="Art. 6" title="Durée, poursuite et résiliation">
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">6.1 Durée initiale.</strong> L'abonnement est conclu pour
            une durée initiale de <strong className="text-ink">douze&nbsp;(12) mois</strong> à
            compter de la mise à disposition effective du service.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">6.2 Poursuite.</strong> À l'issue de cette période, le
            contrat se poursuit pour une durée indéterminée, sans nouvel engagement de durée.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">6.3 Résiliation.</strong> À l'issue de la durée initiale,
            chaque partie peut résilier le contrat à tout moment, par écrit, moyennant un préavis de{' '}
            <strong className="text-ink">deux&nbsp;(2) mois</strong>. Les prestations réalisées
            jusqu'à la date d'effet demeurent dues.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Pendant la durée initiale, le contrat ne peut être résilié que dans les cas prévus au
            paragraphe 6.4, sans préjudice des facultés de résiliation unilatérale reconnues à la
            personne publique par le Code de la commande publique, notamment pour motif d'intérêt
            général.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">6.4 Résiliation pour manquement.</strong> En cas de
            manquement grave de l'une des parties à ses obligations, l'autre partie peut résilier le
            contrat de plein droit trente&nbsp;(30) jours après une mise en demeure adressée par
            lettre recommandée avec accusé de réception restée sans effet.
          </p>
        </LegalSection>

        {/* ============ 7 ============ */}
        <LegalSection number="Art. 7" title="Réversibilité et restitution des données">
          <p className="text-sm text-ink-muted leading-relaxed">
            À tout moment pendant la durée du contrat, le Client peut exporter ses données depuis
            l'application, dans un format structuré et exploitable.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            À la fin du contrat, l'éditeur met à disposition du Client un export complet de ses
            données dans un délai de <strong className="text-ink">trente&nbsp;(30) jours</strong> à
            compter du terme. Cet export comprend les données métier, les documents déposés et les
            journaux nécessaires à la traçabilité.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Les données sont ensuite supprimées définitivement des systèmes de l'éditeur dans un
            délai de trente&nbsp;(30) jours à compter de la mise à disposition de l'export, soit{' '}
            <strong className="text-ink">soixante&nbsp;(60) jours</strong> après le terme du contrat.
            Ce délai permet au Client de vérifier l'intégrité de son export avant l'effacement. Les
            obligations légales de conservation incombant au Client sur ses propres archives publiques
            demeurent de sa responsabilité.
          </p>
        </LegalSection>

        {/* ============ 8 ============ */}
        <LegalSection number="Art. 8" title="Obligations du Client">
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>désigner un interlocuteur référent et communiquer ses coordonnées&nbsp;;</li>
            <li>
              veiller à la confidentialité des identifiants remis à ses agents et à leur révocation
              en cas de départ&nbsp;;
            </li>
            <li>
              fournir les instructions documentées nécessaires au traitement des données personnelles
              (voir article 12)&nbsp;;
            </li>
            <li>s'assurer de la licéité des contenus publiés par ses agents et ses administrés.</li>
          </ul>
        </LegalSection>

        {/* ============ 9 ============ */}
        <LegalSection number="Art. 9" title="Obligations et responsabilité de l'éditeur">
          <p className="text-sm text-ink-muted leading-relaxed">
            L'éditeur est tenu d'une <strong className="text-ink">obligation de moyens</strong>. Il
            met en œuvre les compétences et diligences attendues d'un professionnel de son secteur
            pour assurer la disponibilité et la sécurité du service.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">Il ne saurait être tenu responsable&nbsp;:</p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>des contenus saisis par le Client, ses agents ou ses administrés&nbsp;;</li>
            <li>
              des décisions administratives prises par le Client à l'appui des données du
              service&nbsp;;
            </li>
            <li>
              des indisponibilités imputables à un service tiers (hébergeur, FranceConnect, API
              Particulier, PayFiP, réseau) ou à un cas de force majeure&nbsp;;
            </li>
            <li>de la perte de données résultant d'une manipulation du Client.</li>
          </ul>
          <p className="text-sm text-ink-muted leading-relaxed">
            La responsabilité de l'éditeur est, toutes causes confondues et par année contractuelle,
            limitée au montant des sommes versées par le Client au titre des douze derniers mois.
            Cette limitation ne s'applique pas en cas de faute lourde, de dol ou de dommage corporel.
          </p>
        </LegalSection>

        {/* ============ 10 ============ */}
        <LegalSection number="Art. 10" title="Support">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le support est accessible par courrier électronique et depuis l'application, du{' '}
            <strong className="text-ink">lundi au vendredi de 9&nbsp;h à 17&nbsp;h</strong>, hors
            jours fériés.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            L'éditeur s'engage à accuser réception et à apporter une première réponse qualifiée dans
            un délai de{' '}
            <strong className="text-ink">vingt-quatre&nbsp;(24) heures ouvrées</strong> à compter de
            la réception de la demande.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Ce délai est un <strong className="text-ink">délai de réponse</strong> et non un délai de
            rétablissement&nbsp;: le temps de correction dépend de la nature de l'incident. L'éditeur
            informe le Client de l'avancement et met en œuvre les moyens nécessaires à une résolution
            dans les meilleurs délais.
          </p>
        </LegalSection>

        {/* ============ 11 ============ */}
        <LegalSection number="Art. 11" title="Sauvegardes et continuité">
          <p className="text-sm text-ink-muted leading-relaxed">L'éditeur réalise&nbsp;:</p>
          <ul className="list-disc pl-6 space-y-1.5 text-sm text-ink-muted leading-relaxed marker:text-ink-subtle">
            <li>
              une <strong className="text-ink">sauvegarde quotidienne</strong> complète, chiffrée,
              conservée <strong className="text-ink">trente&nbsp;(30) jours</strong>&nbsp;;
            </li>
            <li>
              une <strong className="text-ink">sauvegarde mensuelle</strong> conservée{' '}
              <strong className="text-ink">douze&nbsp;(12) mois</strong>.
            </li>
          </ul>
          <p className="text-sm text-ink-muted leading-relaxed">
            Les sauvegardes sont hébergées dans l'Union européenne, sur un espace de stockage{' '}
            <strong className="text-ink">
              distinct de l'environnement applicatif et protégé par des identifiants séparés
            </strong>
            , afin qu'une compromission de l'application n'affecte pas les sauvegardes.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Un dispositif d'alerte signale toute absence de sauvegarde réussie depuis plus de
            trente-six&nbsp;(36) heures. L'état des sauvegardes est consultable par le Client depuis
            l'espace d'administration.
          </p>
        </LegalSection>

        {/* ============ 12 ============ */}
        <LegalSection number="Art. 12" title="Données personnelles">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le Client est <strong className="text-ink">responsable de traitement</strong>&nbsp;;
            l'éditeur agit en qualité de <strong className="text-ink">sous-traitant</strong> au sens
            de l'article 28 du RGPD.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Un contrat de sous-traitance, annexé au contrat et signé par les deux parties, encadre ces
            traitements. Il précise les catégories de données, les mesures de sécurité, la liste des
            sous-traitants ultérieurs, le sort des données en fin de contrat et les modalités de
            notification des violations. Le détail des traitements figure dans la{' '}
            <LegalLink href="/politique-confidentialite">politique de confidentialité</LegalLink>.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            L'éditeur notifie au Client toute violation de données à caractère personnel dans un délai
            de <strong className="text-ink">quarante-huit&nbsp;(48) heures</strong> après en avoir
            pris connaissance, afin de lui permettre de respecter son propre délai de notification à
            la CNIL.
          </p>
        </LegalSection>

        {/* ============ 13 ============ */}
        <LegalSection number="Art. 13" title="Accessibilité">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le service fait l'objet d'une démarche de conformité au référentiel général d'amélioration
            de l'accessibilité (RGAA). La{' '}
            <LegalLink href="/accessibilite">déclaration d'accessibilité</LegalLink>, le schéma
            pluriannuel et le plan annuel sont publiés et tenus à jour.
          </p>
        </LegalSection>

        {/* ============ 14 ============ */}
        <LegalSection number="Art. 14" title="Propriété intellectuelle">
          <p className="text-sm text-ink-muted leading-relaxed">
            L'éditeur demeure titulaire de l'ensemble des droits de propriété intellectuelle sur le
            logiciel. Le Client bénéficie d'un droit d'usage personnel, non exclusif et non cessible,
            pour la durée de l'abonnement.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">
              Les données et documents saisis par le Client, ses agents et ses administrés demeurent
              sa propriété pleine et entière.
            </strong>{' '}
            L'éditeur ne les exploite à aucune autre fin que la fourniture du service.
          </p>
        </LegalSection>

        {/* ============ 15 ============ */}
        <LegalSection number="Art. 15" title="Références commerciales">
          <p className="text-sm text-ink-muted leading-relaxed">
            L'éditeur ne peut citer le nom de la collectivité comme référence commerciale qu'avec son{' '}
            <strong className="text-ink">accord écrit préalable</strong>, révocable à tout moment.
          </p>
        </LegalSection>

        {/* ============ 16 ============ */}
        <LegalSection number="Art. 16" title="Droit applicable et litiges">
          <p className="text-sm text-ink-muted leading-relaxed">
            Les présentes conditions générales de vente sont soumises au droit français.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            En cas de différend, les parties s'efforcent de parvenir à une solution amiable. Le Client
            peut saisir le comité consultatif de règlement amiable des différends compétent en matière
            de marchés publics.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            À défaut, les litiges relèvent de la compétence du{' '}
            <strong className="text-ink">tribunal administratif</strong> dans le ressort duquel se
            situe le Client, s'agissant d'un contrat de la commande publique.
          </p>
        </LegalSection>

        {/* Footer */}
        <footer className="pt-6 border-t border-border">
          <p className="font-mono text-xs text-ink-subtle">
            One For All Distribution — SIRET 100&nbsp;323&nbsp;096&nbsp;00014 — 6&nbsp;bis place du
            Bastion, 17100 Saintes · {MENTION_TVA}
          </p>
          <p className="mt-1 font-mono text-xs text-ink-subtle">
            CGV v1.1 — 23 juillet 2026 · contact@oneforall-distribution.fr
          </p>
        </footer>
      </article>
    </PublicLayout>
  );
}
